import React, { useState, useEffect } from 'react';
import { Play, Headphones } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { uiActions } from '../../../store/reducers/uiReducers';

import podcastService from '../../../services/podcastService';
import mediaService from '../../../services/mediaService';
import animationService from '../../../services/animationService';

interface ContentItem {
  id: string;
  type: 'content' | 'podcast' | 'cinema' | 'animation' | 'sneak-peek' | 'sneak_peek';
  title: string;
  description: string;
  duration: string;
  genre?: string;
  category?: string;
  thumbnail: string;
}

const getIcon = (type: string) => {
  // Convert to lowercase and handle backend variations
  const normalizedType = type.toLowerCase().replace('_', '').replace('-', '');
  
  switch (normalizedType) {
    case 'content': 
    case 'cinema': 
    case 'animation':
    case 'sneakpeek':
      return <Play className="w-5 h-5" />;
    case 'podcast': return <Headphones className="w-5 h-5" />;
    default: return <Play className="w-5 h-5" />;
  }
};

const getTypeColor = (type: string) => {
  // Convert to lowercase and handle backend variations
  const normalizedType = type.toLowerCase().replace('_', '').replace('-', '');
  
  switch (normalizedType) {
    case 'content': return 'from-blue-500 to-cyan-600';
    case 'cinema': return 'from-red-500 to-red-600';
    case 'podcast': return 'from-green-500 to-teal-600';
    case 'animation': return 'from-orange-500 to-violet-600';
    case 'sneakpeek': return 'from-orange-500 to-amber-600';
    default: return 'from-gray-500 to-gray-600';
  }
};

const gridSpans = [
  'col-span-1 md:col-span-2 md:row-span-2', // Card 1: large
  'col-span-1 md:col-span-1 md:row-span-1', // Card 2: small
  'col-span-1 md:col-span-1 md:row-span-1', // Card 3: small
  'col-span-1 md:col-span-2 md:row-span-2', // Card 4: large
  'col-span-1 md:col-span-1 md:row-span-1', // Card 5: small
  'col-span-1 md:col-span-1 md:row-span-1', // Card 6: small
];

const ContentTrailerSection: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [contentItems, setContentItems] = useState<ContentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load featured content from various services
  useEffect(() => {
    const loadFeaturedContent = async () => {
      try {
        setLoading(true);
        setError(null);

        // First try to fetch featured content from main services
        const [podcastsResponse, cinemaResponse, contentResponse, animationsResponse] = await Promise.allSettled([
          podcastService.getFeaturedPodcasts(),
          mediaService.getFeaturedCinema(),
          mediaService.getFeaturedContent(),
          animationService.getFeaturedAnimations()
        ]);

        // Collect all featured items from all sources
        const allItems: ContentItem[] = [];

        // Process podcasts
        if (podcastsResponse.status === 'fulfilled' && podcastsResponse.value && podcastsResponse.value.length > 0) {
          const podcasts = podcastsResponse.value
            .filter((podcast: any) => podcast.title) // eslint-disable-line @typescript-eslint/no-explicit-any
            .map((podcast: any) => ({ // eslint-disable-line @typescript-eslint/no-explicit-any
              id: podcast.id,
              type: 'podcast' as const,
              title: podcast.title,
              description: podcast.description || '',
              duration: `${podcast.duration || 30}m`,
              category: podcast.category || 'Podcast',
              thumbnail: podcast.thumbnail,
            }));
          allItems.push(...podcasts);
        }

        // Process Cinema
        if (cinemaResponse.status === 'fulfilled' && cinemaResponse.value && cinemaResponse.value.length > 0) {
          const cinema = cinemaResponse.value
            .filter((cinema: any) => cinema.title) // eslint-disable-line @typescript-eslint/no-explicit-any
            .map((cinema: any) => ({ // eslint-disable-line @typescript-eslint/no-explicit-any
              id: cinema.id,
              type: 'cinema' as const,
              title: cinema.title,
              description: cinema.description || cinema.short_description || '',
              duration: cinema.duration_formatted || '2h',
              category: cinema.category || 'Cinema',
              thumbnail: cinema.thumbnail,
            }));
          allItems.push(...cinema);
        }

        // Process content
        if (contentResponse.status === 'fulfilled' && contentResponse.value && contentResponse.value.length > 0) {
          const content = contentResponse.value
            .filter((item: any) => item.title) // eslint-disable-line @typescript-eslint/no-explicit-any
            .map((item: any) => ({ // eslint-disable-line @typescript-eslint/no-explicit-any
              id: item.id,
              type: 'content' as const,
              title: item.title,
              description: item.description || item.short_description || '',
              duration: item.duration_formatted || '1h',
              category: item.category || 'Content',
              thumbnail: item.thumbnail,
            }));
          allItems.push(...content);
        }

        // Process animations
        if (animationsResponse.status === 'fulfilled' && animationsResponse.value && animationsResponse.value.length > 0) {
          const animations = animationsResponse.value
            .filter((animation: any) => animation.title) // eslint-disable-line @typescript-eslint/no-explicit-any
            .map((animation: any) => ({ // eslint-disable-line @typescript-eslint/no-explicit-any
              id: animation.id,
              type: 'animation' as const,
              title: animation.title,
              description: animation.description || animation.short_description || '',
              duration: animation.duration_formatted || '10m',
              category: animation.category || 'Animation',
              thumbnail: animation.thumbnail,
            }));
          allItems.push(...animations);
        }

        
        // Remove duplicates based on ID, prioritizing animation type over content type
        const uniqueItems = allItems.reduce((acc: ContentItem[], current) => {
          const existingIndex = acc.findIndex(item => item.id === current.id);
          
          if (existingIndex === -1) {
            // No duplicate, add the item
            acc.push(current);
          } else {
            // Duplicate found, prioritize animation type
            const existing = acc[existingIndex];
            if (current.type === 'animation' && existing.type === 'content') {
              // Replace content with animation
              acc[existingIndex] = current;
            }
            // Otherwise keep the existing item (first occurrence)
          }
          
          return acc;
        }, []);
        const shuffledItems = uniqueItems.sort(() => Math.random() - 0.5);
        
        let finalItems = shuffledItems.slice(0, 6);
        
        // If we don't have enough featured content, fallback to regular content
        if (finalItems.length < 6) {
          
          try {
            // Fetch regular content to fill the gap
            const [regularPodcastsResponse, regularCinemaResponse, regularContentResponse] = await Promise.allSettled([
              podcastService.getPodcasts({ page: 1, page_size: 12 }),
              mediaService.getCinema({ page: 1, page_size: 12 }),
              mediaService.getContent({ page: 1, page_size: 12 })
            ]);
            
            const regularItems: ContentItem[] = [];
            
            
            // Process regular podcasts
            if (regularPodcastsResponse.status === 'fulfilled' && regularPodcastsResponse.value?.results) {
              const podcasts = regularPodcastsResponse.value.results
                .filter((podcast: any) => podcast.title) // eslint-disable-line @typescript-eslint/no-explicit-any
                .slice(0, 2) // Limit to 2
                .map((podcast: any) => ({ // eslint-disable-line @typescript-eslint/no-explicit-any
                  id: podcast.id,
                  type: 'podcast' as const,
                  title: podcast.title,
                  description: podcast.description || '',
                  duration: `${podcast.duration || 30}m`,
                  category: podcast.category || 'Podcast',
                  thumbnail: podcast.thumbnail,
                }));
              regularItems.push(...podcasts);
            }
            
            // Process regular cinema
            if (regularCinemaResponse.status === 'fulfilled' && regularCinemaResponse.value?.results) {
              const cinema = regularCinemaResponse.value.results
                .filter((cinema: any) => cinema.title) // eslint-disable-line @typescript-eslint/no-explicit-any
                .slice(0, 2) // Limit to 2
                .map((cinema: any) => ({ // eslint-disable-line @typescript-eslint/no-explicit-any
                  id: cinema.id,
                  type: 'cinema' as const,
                  title: cinema.title,
                  description: cinema.description || cinema.short_description || '',
                  duration: cinema.duration_formatted || '2h',
                  category: cinema.category || 'Cinema',
                  thumbnail: cinema.thumbnail,
                }));
              regularItems.push(...cinema);
            }
            
            // Process regular content
            if (regularContentResponse.status === 'fulfilled' && regularContentResponse.value?.results) {
              const content = regularContentResponse.value.results
                .filter((item: any) => item.title) // eslint-disable-line @typescript-eslint/no-explicit-any
                .slice(0, 2) // Limit to 2
                .map((item: any) => ({ // eslint-disable-line @typescript-eslint/no-explicit-any
                  id: item.id,
                  type: 'content' as const,
                  title: item.title,
                  description: item.description || item.short_description || '',
                  duration: item.duration_formatted || '1h',
                  category: item.category || 'Content',
                  thumbnail: item.thumbnail,
                }));
              regularItems.push(...content);
            }
            
            // Filter out items that are already in finalItems to avoid duplicates
            const existingIds = new Set(finalItems.map(item => item.id));
            const newItems = regularItems.filter(item => !existingIds.has(item.id));
            
            // Add regular items to fill up to 6 total
            const itemsNeeded = 6 - finalItems.length;
            const shuffledRegularItems = newItems.sort(() => Math.random() - 0.5);
            finalItems = [...finalItems, ...shuffledRegularItems.slice(0, itemsNeeded)];
            
          } catch {
            // Failed to fetch fallback content
          }
        }

        setContentItems(finalItems);
      } catch {
        setError('Failed to load featured content');
      } finally {
        setLoading(false);
      }
    };

    loadFeaturedContent();
  }, []);

  // Handle Play Now action
  const handlePlayNow = (item: ContentItem) => {
    // Navigate to appropriate page based on content type
    // The player page handles the 90-second preview for unauthenticated users
    switch (item.type) {
      case 'podcast':
        navigate(`/podcast/${item.id}`);
        break;
      case 'cinema':
        navigate(`/watch/cinema/${item.id}`);
        break;
      case 'content':
        navigate(`/watch/content/${item.id}`);
        break;
      case 'animation':
        navigate(`/animation/${item.id}`);
        break;
      case 'sneak-peek':
      case 'sneak_peek':
        navigate(`/sneak-peeks/${item.id}`);
        break;
      default:
        dispatch(uiActions.addNotification({
          message: `Play Now: ${item.title}`,
          type: 'info'
        }));
    }
  };

  return (
    <div className="min-h-screen bg-white py-16 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-2">Featured Content</h2>
          <p className="text-gray-500">Explore our curated selection of cinemas, podcasts, and contents</p>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading featured content...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="text-center py-12">
            <p className="text-red-600 mb-4">{error}</p>
            <button 
              onClick={() => window.location.reload()}
              className="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700"
            >
              Try Again
            </button>
          </div>
        )}

        {/* Content Grid */}
        {!loading && !error && contentItems.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-4 auto-rows-[220px] md:auto-rows-[200px] lg:auto-rows-[250px] gap-6">
            {contentItems.map((item, idx) => (
              <div
                key={item.id}
                className={`group relative rounded-2xl overflow-hidden border border-slate-200 hover:border-orange-500 transition-transform transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-orange-400 ${gridSpans[idx % gridSpans.length]}`}
              >
                {/* Card image and hover animation */}
                <div className="absolute inset-0 w-full h-full z-0 transition-transform duration-500 group-hover:-translate-y-2 group-hover:scale-105">
                  <img src={item.thumbnail} alt={item.title} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                </div>

                {/* Card badge */}
                <div className={`absolute top-4 left-4 px-3 py-1 rounded-full bg-gradient-to-r ${getTypeColor(item.type)} text-white text-xs font-semibold flex items-center gap-2 shadow-md z-10`}>
                  {getIcon(item.type)}
                  {item.type === 'animation' ? 'Animation' : item.type.charAt(0).toUpperCase() + item.type.slice(1)}
                </div>

                {/* Card content with hover animation for non-story types */}
                <div className="absolute bottom-4 left-4 right-4 text-white z-10 transition-transform duration-500 group-hover:-translate-y-2">
                  <h3 className="text-lg font-bold">{item.title}</h3>
                  <p className="text-sm text-gray-200 line-clamp-2">{item.description}</p>
                </div>

                {/* Play icon overlay */}
                <div 
                  className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition bg-black/30 z-0 cursor-pointer"
                  onClick={() => handlePlayNow(item)}
                >
                  <Play className="w-12 h-12 text-white drop-shadow-lg" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && contentItems.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-600">No featured content available at the moment.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContentTrailerSection;
